import { Avatar, AvatarFallback, AvatarImage } from "design-system/atoms/Avatar"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "design-system/molecules/Command"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "design-system/organisms/Sidebar"
import { cn } from "design-system/utils"
import { Check, ChevronsUpDown } from "lucide-react"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useSwitchCompanyMutation } from "modules/auth/hooks/useSwitchCompanyMutation"
import { useCompanyQuery } from "modules/settings/hooks/useCompanyQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

export function WorkspaceSelector() {
  const { t } = useTranslation()
  const { isMobile, state } = useSidebar()
  const [searchQuery, setSearchQuery] = useState("")

  const { data: employee } = useAuthEmployeeQuery()
  const { data: company } = useCompanyQuery()
  const { mutateAsync: switchCompany } = useSwitchCompanyMutation()

  const organizationCompanies = useMemo(() => employee?.organization.companies ?? [], [employee])
  const hasMultipleCompanies = organizationCompanies.length > 1
  const shouldUseCombobox = organizationCompanies.length > 5

  const currentCompanyInitials = useMemo(
    () =>
      company?.name
        .split(" ")
        .map((word) => word.charAt(0))
        .join("")
        .slice(0, 2)
        .toUpperCase(),
    [company?.name],
  )

  const filteredCompanies = useMemo(() => {
    if (!shouldUseCombobox || !searchQuery) return organizationCompanies
    const query = searchQuery.toLowerCase()
    return organizationCompanies.filter((orgCompany) =>
      orgCompany.name.toLowerCase().includes(query),
    )
  }, [organizationCompanies, searchQuery, shouldUseCombobox])

  const onSwitchCompany = async (companyId: string) => {
    if (companyId === employee?.companyId) return
    await switchCompany({ companyId })
    window.location.href = "/"
  }

  if (!hasMultipleCompanies) return null

  const isCollapsed = state === "collapsed"

  const triggerButton = (
    <SidebarMenuButton
      size="lg"
      className="group-data-[collapsible=icon]:flex hover:bg-primary/5 data-[state=open]:bg-primary/5 hover:text-primary active:bg-primary/5 data-[state=open]:text-primary"
    >
      <Avatar
        className={cn(
          "h-8 w-8 rounded-md bg-muted",
          "group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:w-10 group-data-[collapsible=icon]:h-10 transition-all duration-300 group-data-[collapsible=icon]:mx-auto",
        )}
      >
        <AvatarImage src={company?.logo ?? undefined} alt={company?.name ?? undefined} />
        <AvatarFallback className="rounded-md bg-foreground/10">
          {currentCompanyInitials}
        </AvatarFallback>
      </Avatar>
      <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
        <span className="truncate font-medium">{company?.name}</span>
        <span className="truncate text-xs text-muted-foreground">{t("shared.nav.workspace")}</span>
      </div>
      <ChevronsUpDown className="ml-auto size-4 group-data-[collapsible=icon]:hidden" />
    </SidebarMenuButton>
  )

  return (
    <SidebarMenu className="p-2">
      <SidebarMenuItem className="shadow-sm rounded-lg bg-sidebar-accent">
        {shouldUseCombobox ? (
          <Popover>
            <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
            <PopoverContent
              className="w-(--radix-popover-trigger-width) min-w-56 rounded-lg p-0 data-[state=open]:bg-sidebar-accent"
              side={isCollapsed && !isMobile ? "right" : "bottom"}
              align="start"
              sideOffset={4}
            >
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={t("shared.nav.searchWorkspace")}
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  <CommandEmpty className="text-muted-foreground text-center pt-2 text-xs">
                    {t("shared.nav.noWorkspacesFound")}
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredCompanies.map((orgCompany) => {
                      const isSelected = employee?.companyId === orgCompany.id
                      const companyInitials = orgCompany.name
                        .split(" ")
                        .map((word) => word.charAt(0))
                        .join("")
                        .slice(0, 2)
                        .toUpperCase()

                      return (
                        <CommandItem
                          key={orgCompany.id}
                          value={orgCompany.id}
                          onSelect={() => onSwitchCompany(orgCompany.id)}
                          className={cn(
                            "flex items-center gap-2 cursor-pointer py-2",
                            isSelected && "bg-primary/5",
                          )}
                        >
                          <Check
                            className={cn("h-4 w-4 shrink-0", {
                              "opacity-100 text-primary": isSelected,
                              "opacity-0": !isSelected,
                            })}
                          />
                          <Avatar className="h-7 w-7 rounded-md">
                            <AvatarImage src={orgCompany.logo ?? undefined} alt={orgCompany.name} />
                            <AvatarFallback className="rounded-md text-xs bg-foreground/10">
                              {companyInitials}
                            </AvatarFallback>
                          </Avatar>
                          <span className="flex-1 truncate">{orgCompany.name}</span>
                        </CommandItem>
                      )
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg data-[state=open]:bg-sidebar-accent"
              side={isCollapsed && !isMobile ? "right" : "bottom"}
              align="start"
              sideOffset={4}
            >
              <div className="flex flex-col gap-1 max-h-60 overflow-y-auto p-1">
                {organizationCompanies.map((orgCompany) => {
                  const isSelected = employee?.companyId === orgCompany.id
                  const companyInitials = orgCompany.name
                    .split(" ")
                    .map((word) => word.charAt(0))
                    .join("")
                    .slice(0, 2)
                    .toUpperCase()

                  return (
                    <DropdownMenuItem
                      key={orgCompany.id}
                      onClick={() => onSwitchCompany(orgCompany.id)}
                      className={cn(
                        "flex items-center gap-2 cursor-pointer py-2",
                        isSelected && "bg-primary/5",
                      )}
                    >
                      <Avatar className="h-7 w-7 rounded-md">
                        <AvatarImage src={orgCompany.logo ?? undefined} alt={orgCompany.name} />
                        <AvatarFallback className="rounded-md text-xs bg-foreground/10">
                          {companyInitials}
                        </AvatarFallback>
                      </Avatar>
                      <span className="flex-1 truncate">{orgCompany.name}</span>
                      {isSelected && <Check size={16} className="text-primary shrink-0" />}
                    </DropdownMenuItem>
                  )
                })}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
